/**
 * One market's book, drilled into from Exposure.
 *
 * The product being replaced stopped at an event-level number: you could see
 * that something was exposed, but not which outcome hurt, never which member
 * put you there, and never at what price. This page answers all three — the
 * ladder says which runner is bad, the book table's tree says whose money it
 * is, and the hedge box says what it would cost to level.
 *
 * Two invariants govern everything below.
 *
 *  I1 — Simulation is contagious and always visible. No simulated figure ever
 *       feeds a computation presented as real, and anything computed FROM one
 *       inherits the marker. The marker is three layers deep, because a chip
 *       alone is a screenshot away from being lost: the literal word
 *       "(simulated)" inside the row's own text so it survives copy, paste and
 *       a screen reader; a warn Chip; and an always-visible sentence in the
 *       group header — never a tooltip alone, because a `title` is not keyboard
 *       reachable and that sentence is what makes the feature safe.
 *
 *  I2 — A stale projection blanks PROJECTIONS, not RECORDS. The ladder, the
 *       book table, the tree and the hedge output are the exposure projection
 *       and are replaced by the stale banner. The bet log below is a record of
 *       what was placed, not a projection of what is owed, so it keeps
 *       rendering. This is the one judgement call on the page worth flagging in
 *       the PR description.
 */

import { useMemo } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Button, Card, Chip, Dot, Icon, Kpi, Segmented, Skeleton, StateBlock } from '@w7/ui'
import { AsOf, StaleProjection } from '@/app/bits'
import { useMarketBook, useSession } from '@/app/queries'
import { count, lll, money, moneyCompact, pct, toneOf } from '@/lib/format'
import { ROLE_LABEL, type EventExposure, type MarketExposure } from '@/data/types'
import { BookPanel } from './BookPanel'
import { MarketBets } from './MarketBets'
import { PriceLadderPanel } from './PriceLadderPanel'
import { readBetFilters } from './betFilters'
import './market-book.css'

/** Above this many runners the two-panel split stops being readable. */
const STACK_ABOVE_SELECTIONS = 4

export function MarketBookPage() {
  const { marketId } = useParams<{ marketId: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const basis = searchParams.get('basis') === 'my' ? 'my' : 'total'
  const filters = readBetFilters(searchParams)

  /** replace, not push: typing four characters into the member search must not
   *  cost four presses of the back button. */
  const patch = (next: Record<string, string | null>) => {
    const params = new URLSearchParams(searchParams)
    for (const [key, value] of Object.entries(next)) {
      params.delete(key)
      if (value !== null) params.set(key, value)
    }
    setSearchParams(params, { replace: true })
  }

  const { data: session } = useSession()
  const share = session?.account.share.sportsbook
  const { data, isLoading, isError, error, refetch, isPlaceholderData } = useMarketBook(
    marketId,
    basis,
  )

  const book = data?.value ?? null
  const market = book?.market ?? null

  const extremes = useMemo(() => {
    const first = market?.selections[0]
    if (!market || !first) return null
    let worst = first
    let best = first
    for (const selection of market.selections) {
      if (selection.net < worst.net) worst = selection
      if (selection.net > best.net) best = selection
    }
    return { worst, best }
  }, [market])

  const betLog =
    marketId ? (
      <MarketBets
        marketId={marketId}
        market={market}
        eventName={book?.event.eventName ?? ''}
        filters={filters}
        onPatch={patch}
      />
    ) : null

  /* --------------------------------------------------------------- states -- */

  if (data?.stale) {
    return (
      <>
        <MarketHead event={book?.event ?? null} market={market} basis={basis} asOf={data.asOf} />
        <StaleProjection asOf={data.asOf} />
        <p className="w7-card-sub" style={{ margin: 'var(--w7-s3) 0 var(--w7-s5)' }}>
          The bet list below is a record of what was placed and is still shown; only the projected
          book is hidden.
        </p>
        {betLog}
      </>
    )
  }

  if (!isLoading && !book) {
    return (
      <>
        <MarketHead event={null} market={null} basis={basis} />
        <StateBlock
          icon={isError ? 'warning' : 'search'}
          title={isError ? 'No data found' : 'That market is not in your book'}
          actions={
            <Button
              variant="secondary"
              icon={isError ? 'refresh' : 'chevron-left'}
              onClick={() => (isError ? void refetch() : navigate('/exposure'))}
            >
              {isError ? 'Try again' : 'Back to exposure'}
            </Button>
          }
        >
          {isError
            ? (error as Error)?.message ||
              'The market book could not be reached. Nothing is invented while it is down.'
            : 'The link may be stale, or the market sits outside your downline. Markets appear here only while someone beneath you has an open position on them.'}
        </StateBlock>
      </>
    )
  }

  const stacked = (market?.selections.length ?? 0) > STACK_ABOVE_SELECTIONS

  return (
    <>
      <MarketHead
        event={book?.event ?? null}
        market={market}
        basis={basis}
        asOf={data?.asOf}
        updating={isPlaceholderData}
      />

      {/* ------------------------------------------------------------ KPIs -- */}
      <div className="w7-grid w7-grid-4">
        <Kpi
          label="Worst case"
          value={
            market ? moneyCompact(market.worstCase) : <Skeleton width={104} height={26} />
          }
          exact={money(market?.worstCase)}
          // `toneOf` is the one place that decides green/red, and it returns
          // neutral at exactly 0 — a flat book is calm, not a win.
          tone={market ? toneOf(market.worstCase) : undefined}
          accent
          meta={
            extremes ? (
              `If ${extremes.worst.name} wins`
            ) : (
              <Skeleton width={132} height={12} />
            )
          }
        />
        <Kpi
          label="Best case"
          value={market ? moneyCompact(market.bestCase) : <Skeleton width={104} height={26} />}
          exact={money(market?.bestCase)}
          tone={market ? toneOf(market.bestCase) : undefined}
          meta={
            extremes ? `If ${extremes.best.name} wins` : <Skeleton width={132} height={12} />
          }
        />
        <Kpi
          label="Matched"
          value={market ? moneyCompact(market.matched) : <Skeleton width={104} height={26} />}
          exact={money(market?.matched)}
          meta={
            market ? (
              `${moneyCompact(market.unmatched)} still unmatched, carrying no liability`
            ) : (
              <Skeleton width={132} height={12} />
            )
          }
        />
        <Kpi
          label="Open bets"
          value={market ? count(market.betCount) : <Skeleton width={104} height={26} />}
          meta={
            market ? (
              // `memberCount` counts MEMBERS. The leaf tier is never "Player" —
              // note that the casino market "Table Round" has a SELECTION named
              // Player, which is exchange data and is rendered verbatim.
              `From ${count(market.memberCount)} ${
                market.memberCount === 1 ? ROLE_LABEL.player : `${ROLE_LABEL.player}s`
              }`
            ) : (
              <Skeleton width={132} height={12} />
            )
          }
        />
      </div>

      {/* ----------------------------------------------------------- basis -- */}
      <Card style={{ marginTop: 'var(--w7-s5)', marginBottom: 'var(--w7-s5)' }}>
        <div className="w7-mb-basis">
          <Segmented
            label="Exposure basis"
            value={basis}
            onChange={(next) => patch({ basis: next === 'total' ? null : next })}
            items={[
              { id: 'my', label: 'My share' },
              { id: 'total', label: 'Total book' },
            ]}
          />
          {/* Same sentence as the exposure grid, word for word. Two names or two
              explanations for one control across two screens is worse than
              diverging from the recording's "My PT". */}
          <p className="w7-mb-basis-note">
            My share applies{' '}
            {share === undefined ? 'your sportsbook share' : `your ${pct(share, 0)} sportsbook share`}{' '}
            to every position. Total book is the whole downline&rsquo;s liability, before the split.
          </p>
        </div>

        <div className="w7-mb-panels" data-stack={stacked ? 'true' : undefined}>
          <PriceLadderPanel
            eventName={book?.event.eventName ?? ''}
            marketName={market?.marketName ?? ''}
            marketType={market?.marketType ?? null}
            prices={book?.prices ?? []}
            positions={market?.selections ?? []}
            loading={isLoading || !market}
          />
          <BookPanel
            marketId={marketId ?? ''}
            eventName={book?.event.eventName ?? ''}
            book={book}
            basis={basis}
            loading={isLoading}
            onSwitchBasis={() => patch({ basis: null })}
          />
        </div>
      </Card>

      {betLog}
    </>
  )
}

/* ------------------------------------------------------------------ head -- */

function MarketHead({
  event,
  market,
  basis,
  asOf,
  updating,
}: {
  event: EventExposure | null
  market: MarketExposure | null
  basis: 'my' | 'total'
  asOf?: string
  updating?: boolean
}) {
  const started = event ? Date.parse(event.startsAt) <= Date.now() : false
  // The basis the operator chose on the grid survives the drill-in AND the
  // drill-out. Losing it on the way back is how someone reads a total-book
  // figure believing it is their share.
  const back = basis === 'total' ? '/exposure' : `/exposure?basis=${basis}`

  return (
    <div className="w7-page-head">
      <div style={{ minWidth: 0 }}>
        <Link
          to={back}
          className="w7-link w7-row"
          style={{ gap: 4, fontSize: 12.5, marginBottom: 6, width: 'fit-content' }}
        >
          <Icon name="chevron-left" size={13} />
          Exposure
        </Link>
        <h1 className="w7-page-title">
          {event ? event.eventName : <Skeleton width={240} height={22} />}
        </h1>
        <div className="w7-row-wrap" style={{ gap: 'var(--w7-s2)', marginTop: 6 }}>
          {market ? (
            <>
              <span className="w7-card-sub">{market.marketName}</span>
              {/* Suppressed when the type IS the name — the exposure grid makes
                  the same call, and "Bookmaker / Bookmaker" reads as a bug. */}
              {market.marketType !== market.marketName ? (
                <Chip size="sm" tone="brand">
                  {market.marketType}
                </Chip>
              ) : null}
            </>
          ) : (
            <Skeleton width={180} height={14} />
          )}
          {event ? (
            <>
              <Chip size="sm" icon="sport">
                {event.sport}
              </Chip>
              {/* Brand orange, NOT red: on this console red means the book paid
                  out, and a live badge is not a loss. */}
              {event.inPlay ? (
                <Chip size="sm" tone="brand">
                  <Dot tone="brand" pulse />
                  Live
                </Chip>
              ) : null}
              <span className="w7-card-sub">
                {started ? 'Started' : 'Starts'} {lll(event.startsAt)}
              </span>
            </>
          ) : null}
        </div>
      </div>
      <div className="w7-row-wrap" style={{ marginLeft: 'auto' }}>
        {updating ? <span className="w7-asof">Updating…</span> : null}
        <AsOf at={asOf} polls />
      </div>
    </div>
  )
}
